import { useContext, useEffect, useState, type ReactElement } from "react";
import { useLocation, useSearchParams } from "react-router";

import { fetchSession, getJson } from "../../api/client.js";
import { LeaveContext, queryValue } from "../../app/navigation.js";
import { useGo } from "../../app/use-go.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary, TextField } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";
import { totpRules } from "../../forms/view-model-rules.js";

import { EmailCodeOffer } from "./email-code-pages.js";

/** 認証アプリの登録に要るもの（<c>GET /api/account/totp/enrollment</c>）。 */
interface Enrollment {
    readonly secret: string;
    readonly qrCodeImage: string;
}

/**
 * <b>data: の画像だけを受ける。</b>ほかの URL を入れると、画面の外へ取りに行く。
 * 受け取るまでは透明な 1 ピクセル（壊れた画像の印を出さない）。
 */
const blankImage = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/** 登録済みの人がログインした後にこの画面を開いたとき、アカウント設定で出す知らせ。 */
export const alreadyEnrolledNotice =
    "二段階認証は登録済みです。付け替えるときは、いったん解除してから登録し直してください。";

const imageOf = (enrollment: Enrollment | undefined): string =>
    enrollment?.qrCodeImage.startsWith("data:image/") === true ? enrollment.qrCodeImage : blankImage;

/**
 * 認証アプリの登録（二段階認証は全員必須。未登録ならログインの途中でここへ来る）。
 *
 * <b>鍵は開いてから受け取る。</b>人ごとに違い、書き出す時点では無い。
 * 受け取れなければ（ログインの途中ではない）ログインからやり直してもらう。
 *
 * <b>登録済みの人には API が鍵を返さない（409）。</b>このときは、ログインした後ならアカウント設定へ移して
 * 解除してから登録し直すよう知らせ、ログインの途中なら確認コードの入力の画面へ移す（パスワードの後に
 * 登録済みの人が進む画面。戻り先と「ログインしたままにする」の URL の値はそのまま渡す）。
 * どちらかは <c>/api/session</c> で見る。行き先を変えるだけで、2 段目を越えるかどうかは API が決める。
 *
 * <b>メールで受け取る道はログインの途中だけ出す。</b>ログインの途中なら API が
 * <c>?rememberMe=</c> を付けて送ってくる（アカウント設定の「設定する」からは付かない）。
 * 出し分けは見た目だけで、途中でなければ API が 401 で断る。
 */
export function EnableTotpPage(): ReactElement {
    usePageMeta("二段階認証の設定");
    const form = useApiForm("/api/account/totp/enrollment", totpRules);
    const leave = useContext(LeaveContext);
    const go = useGo();
    const { search: query } = useLocation();
    const [search] = useSearchParams();
    const [enrollment, setEnrollment] = useState<Enrollment>();
    const signingIn = queryValue(search, "rememberMe") !== undefined;

    useEffect(() => {
        void getJson("/api/account/totp/enrollment").then((result) => {
            if (result.ok) {
                setEnrollment(result.value as Enrollment);
            } else if (result.status === 409) {
                void fetchSession()
                    .then((session) => {
                        if (session.signedIn) {
                            go("/Account", { notice: alreadyEnrolledNotice });
                        } else {
                            go(`/Account/VerifyTotpLogin${query}`);
                        }
                    })
                    // 状態を聞けなければ、ほかの失敗と同じくログインからやり直してもらう
                    .catch(() => { leave("/Account/Login"); });
            } else {
                leave("/Account/Login");
            }
        });
    }, [leave, go, query]);

    return (
        <div className="page max-w-lg">
            <div className="panel">
                <h1 className="page-title">二段階認証の設定</h1>
                <p className="page-lead">
                    認証アプリ（Google Authenticator、Microsoft Authenticator など）で
                    QR コードを読み取り、表示された確認コードを入力してください。
                </p>

                <FormSummary messages={form.summary} />

                <ol className="mb-7 space-y-5">
                    <li>
                        <p className="mb-3 text-sm font-medium text-slate-700">1. QR コードを読み取る</p>
                        <div className="flex justify-center rounded-lg border border-slate-200 bg-white p-5">
                            <img src={imageOf(enrollment)} alt="二段階認証の QR コード" width={220} height={220} data-totp-qr="" />
                        </div>
                    </li>
                    <li>
                        <p className="mb-2 text-sm font-medium text-slate-700">
                            2. QR コードを読み取れない場合はこのキーを手入力する
                        </p>
                        <code
                            data-totp-secret={enrollment?.secret ?? ""}
                            className="block rounded-lg bg-slate-100 px-4 py-3 text-center text-sm font-mono break-all select-all"
                        >
                            {enrollment?.secret}
                        </code>
                    </li>
                </ol>

                <form method="post" noValidate onSubmit={form.onSubmit} onChange={form.onChange} onBlur={form.onBlur}>
                    <input type="hidden" name="ReturnUrl" value={queryValue(search, "returnUrl") ?? ""} />
                    <input type="hidden" name="RememberMe" value={queryValue(search, "rememberMe") ?? "false"} />

                    <TextField
                        label="3. 表示された確認コードを入力する" name="Code" required
                        inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                        placeholder="000000" autoFocus
                        wrapperClassName="mb-7"
                        inputClassName="text-center text-2xl tracking-[0.4em]"
                        error={form.fieldError("Code")}
                    />

                    <button type="submit" className="btn-primary w-full py-3" disabled={form.busy}>
                        設定を完了する
                    </button>
                </form>

                {signingIn
                    ? <EmailCodeOffer lead="認証アプリの登録がすぐにできない方は、メールで認証できます。" />
                    : null}
            </div>
        </div>
    );
}
